import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  onAuthStateChanged,
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from "firebase/auth";
import { auth } from "../config/config-firebase";
import Alert from "react-s-alert";
import "../importData/LoginScreen.css";
import axios from "axios";
import API_BASE from "../config/api";
import { countryCodes } from "../constants/countryCodes";
import { ACCESS_DISABLED_MESSAGE, isDefaultStoreRow } from "../hooks/useMerchantStores";
import { clearStaleAdminMerchantModeFlag } from "../utils/adminMerchantMode";

function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const recaptchaInitialized = useRef(false); // 🔐 KEY FIX
  // While verifyOTP is running its own merchantUserDetails check (including
  // the disabled-account gate below), suppress this listener's blanket
  // "already signed in -> /home" redirect. Without this, Firebase's OTP
  // confirm() completes the sign-in and fires this listener almost
  // immediately — well before the merchantUserDetails round trip resolves —
  // so a disabled team member would get bounced to /home before we ever
  // find out they're disabled.
  const suppressAutoRedirectRef = useRef(false);

  /* ---------------- STATE ---------------- */
  const [countryCode, setCountryCode] = useState("+1");
  const [mobile, setMobile] = useState("");
  const [otp, setOTP] = useState("");
  const [expandForm, setExpandForm] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const [disabledMessage, setDisabledMessage] = useState("");

  /* ---------------- DISABLED-MEMBER REDIRECT MESSAGE ---------------- */
  // Set when MerchantHome's is_teammember_active check signs a disabled
  // team member out and sends them back here mid-session.
  useEffect(() => {
    if (location.state?.disabledMessage) {
      setDisabledMessage(location.state.disabledMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  /* ---------------- AUTH CHECK ---------------- */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !suppressAutoRedirectRef.current) {
        navigate("/home");
      }
    });

    setupReCaptchaOnce();

    return () => unsubscribe();
  }, [navigate]);

  /* ---------------- RECAPTCHA (FIXED) ---------------- */
  const setupReCaptchaOnce = () => {
    if (recaptchaInitialized.current) return; // 🔐 PREVENT DUPLICATES

    try {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            console.log("reCAPTCHA solved");
          },
        }
      );

      recaptchaInitialized.current = true;
    } catch (err) {
      console.error("reCAPTCHA init failed:", err);
    }
  };

  /* ---------------- CHECK MOBILE ---------------- */
  const checkMobileBeforeOTP = async (e) => {
    e.preventDefault();
    const fullPhoneNumber = `${countryCode}${mobile}`;

    try {
      const { data } = await axios.post(
        `${API_BASE}/checkMobile`,
        { mobile: fullPhoneNumber }
      );

      if (data.exists === true) {
        requestOTP(fullPhoneNumber);
      } else {
        Alert.error("The mobile number appears to be unregistered.");
      }
    } catch {
      Alert.error("Server error while checking mobile");
    }
  };

  /* ---------------- REQUEST OTP ---------------- */
  const requestOTP = async (phone) => {
    setProcessing(true);

    try {
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phone,
        window.recaptchaVerifier
      );

      window.confirmationResult = confirmationResult;
      setExpandForm(true);
      Alert.success("OTP has been sent.");
    } catch (error) {
      console.error(error);
      Alert.error("Unable to send OTP. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  /* ---------------- VERIFY OTP ---------------- */
  const verifyOTP = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setDisabledMessage("");
    suppressAutoRedirectRef.current = true;

    const fullPhoneNumber = `${countryCode}${mobile}`;

    try {
      // Firebase completes the sign-in right here — before we've confirmed
      // with the backend that this account is allowed to use the store.
      const result = await window.confirmationResult.confirm(otp);
      const idToken = await result.user.getIdToken();

      const res = await axios.post(
        `${API_BASE}/merchantUserDetails`,
        { mobile: fullPhoneNumber, idToken }
      );

      if (res.data.success) {
        // merchantUserDetails returns account_user.id (e.g. 243), NOT the
        // store's customer_id. Storing that id under "customer_id" made every
        // store-scoped screen (inventory import, online ordering, ...) read and
        // write against a store id that doesn't exist. Resolve the real store
        // customer_id the same way PromoLogin does, and only fall back to the
        // account id if the store lookup fails.
        const accountUserId = res.data.data;
        let customerId = accountUserId;
        try {
          const storesRes = await axios.post(`${API_BASE}/getMerchantStoreDetails`, {
            userId: Number(accountUserId),
          });
          const stores = Array.isArray(storesRes.data?.data) ? storesRes.data.data : [];
          const targetStore = stores.find(isDefaultStoreRow) || stores[0];
          if (targetStore?.customer_id) {
            customerId = targetStore.customer_id;
          }
        } catch (storeErr) {
          console.error(
            "getMerchantStoreDetails failed, falling back to merchantUserDetails value:",
            storeErr,
          );
        }

        localStorage.setItem("phone", fullPhoneNumber);
        localStorage.setItem("customer_id", customerId);
        // Real merchant login — don't inherit a stale Admin Merchant Mode
        // flag from a previous impersonation session on this browser.
        clearStaleAdminMerchantModeFlag();
        Alert.success("You're successfully logged in!");
        navigate("/modes", { state: { customerId } });
      } else {
        // success:false with a 2xx status isn't currently returned by
        // merchantUserDetails, but handle it the same as the 403 case below
        // rather than silently leaving the Firebase session signed in.
        await auth.signOut();
        setDisabledMessage(ACCESS_DISABLED_MESSAGE);
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        // Backend rejected this account (disabled team member) — the OTP
        // step already signed them into Firebase, so undo that too.
        await auth.signOut();
        setDisabledMessage(ACCESS_DISABLED_MESSAGE);
      } else {
        Alert.error("Invalid OTP");
      }
    } finally {
      suppressAutoRedirectRef.current = false;
      setProcessing(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="login-wrapper">
      <div className="login-box">
        <h1 className="title">Login to EzStore</h1>

        {!expandForm ? (
          <form onSubmit={checkMobileBeforeOTP} className="form">
            <div className="phone-wrapper">
              <select
                className="country-select"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                {countryCodes.map((c) => (
                  <option key={c.code} value={c.dial_code}>
                    {c.dial_code}
                  </option>
                ))}
              </select>

              <input
                type="tel"
                className="phone-input"
                placeholder="Phone number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="btn" disabled={isProcessing}>
              {isProcessing ? "Sending..." : "Login with OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyOTP} className="form">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              className="input"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOTP(e.target.value.replace(/\D/g, ""))}
              required
            />

            <button type="submit" className="btn" disabled={isProcessing}>
              {isProcessing ? "Verifying..." : "Verify Code"}
            </button>
          </form>
        )}

        {disabledMessage && (
          <p className="access-disabled-message">{disabledMessage}</p>
        )}

        {/* REQUIRED */}
        <div id="recaptcha-container"></div>

        <p className="footer">Secure login powered by Firebase</p>
        <Alert stack={{ limit: 3 }} timeout={3000} />
      </div>
    </div>
  );
}

export default LoginScreen;
